
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { generatePreviewCertificate } from '@/utils/certificateUtils';

interface CertificatePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string | null;
}

export function CertificatePreviewDialog({ open, onOpenChange, templateId }: CertificatePreviewDialogProps) {
  const [previewHtml, setPreviewHtml] = useState<string>('');
  
  const { data: template, isLoading } = useQuery({
    queryKey: ['certificateTemplate', templateId],
    queryFn: async () => {
      if (!templateId) return null;
      
      const { data, error } = await supabase
        .from('certificate_templates')
        .select('*')
        .eq('id', templateId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!templateId && open,
  });
  
  useEffect(() => {
    if (template) {
      // Generate a preview HTML with sample data
      const html = generateTemplateHtml(template);
      setPreviewHtml(html);
    }
  }, [template]);
  
  const generateTemplateHtml = (templateData: any): string => {
    // Create the default certificate template with placeholders
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Certificate Preview</title>
  <link href="https://fonts.googleapis.com/css2?family=${templateData.font_family}:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
    body {
      font-family: '${templateData.font_family}', sans-serif;
      margin: 0;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background-color: ${templateData.background_color};
    }
    .certificate {
      width: 800px;
      padding: 40px;
      background-color: #ffffff;
      border: 2px solid ${templateData.border_color};
      border-radius: 10px;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      text-align: center;
    }
    .certificate h1 {
      color: ${templateData.border_color};
      font-size: 36px;
      font-weight: 700;
      margin-bottom: 10px;
    }
    .certificate h2 {
      font-size: 18px;
      font-weight: 400;
      margin-bottom: 5px;
    }
    .certificate h3 {
      font-size: 14px;
      font-weight: 400;
      margin-bottom: 20px;
    }
    .divider {
      width: 50%;
      height: 2px;
      background-color: #FBBF24;
      margin: 20px auto;
    }
    .certificate p {
      font-size: 16px;
      margin: 10px 0;
      color: ${templateData.text_color};
    }
    .certificate .highlight {
      font-weight: 600;
    }
    .certificate table {
      width: 80%;
      margin: 20px auto;
      border-collapse: collapse;
      font-size: 14px;
    }
    .certificate table th,
    .certificate table td {
      padding: 10px;
      text-align: left;
      border-bottom: 1px solid #ddd;
      color: ${templateData.text_color};
    }
    .certificate table th {
      font-weight: 600;
      width: 40%;
    }
    .certificate .footer {
      font-size: 12px;
      color: #666;
      margin-top: 30px;
    }
    @media print {
      body {
        background-color: #ffffff;
      }
      .certificate {
        box-shadow: none;
        border: none;
      }
    }
    ${templateData.custom_css || ''}
  </style>
</head>
<body>
  <div class="certificate">
    <h1>Certificate of Completion</h1>
    <h2>Florida Driver Improvement Course</h2>
    <h3>Presented by DRIVE COURSES, LLC</h3>
    <h3>Provider Phone: (800) 555-1234</h3>
    <div class="divider"></div>
    <p>This certifies that</p>
    <p class="highlight">John Doe</p>
    <p>has successfully completed the Florida Driver Improvement Course</p>
    <p>on <span class="highlight">May 3, 2025</span>.</p>
    <table>
      <tr>
        <th>Certificate Number</th>
        <td>FL-BDI-2025-12345</td>
      </tr>
      <tr>
        <th>Course Completion Date</th>
        <td>May 3, 2025</td>
      </tr>
      <tr>
        <th>Citation Number</th>
        <td>FL12345</td>
      </tr>
      <tr>
        <th>Citation County</th>
        <td>Miami-Dade</td>
      </tr>
      <tr>
        <th>Driver License Number</th>
        <td>A123-456-78-901-2</td>
      </tr>
      <tr>
        <th>Date of Birth</th>
        <td>January 15, 1990</td>
      </tr>
      <tr>
        <th>Reason for Attending</th>
        <td>Court-Ordered</td>
      </tr>
    </table>
    <div class="footer">
      <p>Issued by DRIVE COURSES, LLC in compliance with the Florida Department of Highway Safety and Motor Vehicles (FLHSMV).</p>
      <p>This certificate is valid for submission to the Clerk of Court or for personal records.</p>
    </div>
  </div>
</body>
</html>`;
    
    return html;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[850px] w-[90vw] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{template?.name || 'Certificate Preview'}</DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-[500px]">
            <p>Loading preview...</p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[80vh]">
            <iframe
              srcDoc={previewHtml}
              title="Certificate Preview"
              className="w-full h-[600px] border-0"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
